/**
 * Converts a label to a kebab-case slug for use in data-testid attributes.
 *
 * Strips to `[a-z0-9-]` because these become selectors: real labels carry punctuation
 * (`Save & Add Another`, `Enable SSO & Finish`) which is awkward to match on, and merchant-created
 * text (tag labels, template names) can carry anything. NFD-normalizes first so accented labels
 * degrade to legible ASCII — `Excluir Automação` becomes `excluir-automacao`, not `excluir-automa-o`.
 *
 * A label in a non-Latin script (CJK, Arabic, Cyrillic) has no `[a-z0-9]` to keep, so it slugs to
 * `''` and the caller's fallback applies — every such label yields the same id. Pre-existing and
 * shared by every `[a-z0-9]`-based slug in this repo; only en-US and pt-BR ship today.
 *
 * Apostrophes are deleted rather than treated as separators, so `Staff's Friendliness` slugs to
 * `staffs-friendliness` and not `staff-s-friendliness` — an apostrophe sits inside a word, unlike
 * the punctuation around it.
 *
 * Returns `''` for null/undefined AND for a label with nothing slug-able. Callers that need a
 * non-empty id supply their own fallback (`|| 'confirm'`), since a sensible default is
 * caller-specific rather than universal.
 */
export const slugify = (text?: string | null): string =>
  text
    ? text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2019]/g, '')
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : ''

/**
 * Slugifies a list of labels, disambiguating any that collide.
 *
 * `slugify` is lossy — it lowercases and collapses every `[^a-z0-9]+` run — so two DIFFERENT labels
 * can produce the same slug (`'menu.pdf'` and `'Menu.PDF'` both give `'menu-pdf'`). Harmless in a
 * heading; a defect in a `data-testid`, where duplicates make a locator ambiguous and break the
 * uniqueness rule in `.claude/rules/vue/playwright-selectors.md`.
 *
 * Repeats take a positional suffix in list order, so the first occurrence keeps the clean slug.
 * Order-dependent by necessity: nothing else distinguishes inputs that slugify identically.
 *
 * @param {string[]} labels Labels in render order
 * @returns {string[]} Slugs, unique within the list, aligned to the input order
 *
 * @example
 *   uniqueSlugs(['menu.pdf', 'Menu.PDF'])  // ['menu-pdf', 'menu-pdf-2']
 */
export const uniqueSlugs = (labels: string[]): string[] => {
  const taken = new Set<string>()
  const nextSuffix = new Map<string, number>()

  return labels.map((label) => {
    // `|| 'file'` because slugify returns '' for a label with no [a-z0-9] at all (e.g. a CJK
    // filename), which would emit a testid ending in a bare '-'. Every other caller supplies its
    // own fallback for the same reason — see slugify's JSDoc above.
    const base = slugify(label) || 'file'
    // Checked against the slugs already EMITTED, not a per-base count: a generated suffix can
    // collide with a literal input of the same shape (['menu.pdf', 'menu.pdf-2', 'Menu.PDF']).
    let suffix = nextSuffix.get(base) ?? 1
    let candidate = base

    while (taken.has(candidate)) {
      suffix += 1
      candidate = `${base}-${suffix}`
    }

    nextSuffix.set(base, suffix)
    taken.add(candidate)

    return candidate
  })
}

export function useDataTestid() {
  const generateDataTestid = (sectionTag: string, componentTag: string, htmlTag?: string): string => {
    const sanitize = (value: string): string => (typeof value === 'string' ? value.replace(/\s+/g, '-') : '')
    return htmlTag ? `${sectionTag}-${componentTag}-${sanitize(htmlTag)}` : `${sectionTag}-${componentTag}`
  }

  return { generateDataTestid, slugify, uniqueSlugs }
}
